import React, { useState, useRef, useCallback } from 'react'
import * as d3 from 'd3'
import { ExtendedFeatureCollection, ExtendedFeature } from 'd3'
import { cantons } from '../../cantons.js'
import './styles.scss'

const width = 1000
const height = 700

interface Props {
  filteredCommunes: ExtendedFeatureCollection
}

export const Map = ({ filteredCommunes }: Props) => {
  const [popupPosition, setPopupPosition] = useState([0, 0, 0, 0])
  const [communeData, setCommuneData] = useState(null)
  const mapContainer = useRef(null)

  const handleChangeItem = useCallback((properties, position) => {
    const parentPosition = mapContainer.current.getBoundingClientRect()
    if (properties) {
      setPopupPosition([
        position.x - parentPosition.x,
        position.y - parentPosition.y,
        position.x > parentPosition.x + parentPosition.width / 2,
        position.y > parentPosition.y + parentPosition.height / 2,
      ])
      setCommuneData(properties)
    } else {
      setCommuneData(null)
    }
  }, [])

  return (
    <div ref={mapContainer}>
      {communeData && (
        <div
          className={`popup ui raised compact segment ${
            popupPosition[2] ? 'pleft' : ''
          } ${popupPosition[3] ? 'ptop' : ''}`}
          style={{
            transform: `translate(${popupPosition[0]}px, ${popupPosition[1]}px)`,
          }}
        >
          <div>
            <b>{communeData.GMDNAME}</b>
          </div>
          <div>{cantons.find((c) => c.id === communeData.KTNR).name}</div>{' '}
          <div>{Math.round((1 - communeData.distance) * 10000) / 100}% de similarité</div>
        </div>
      )}
      <MapBackground
        hoverHandler={handleChangeItem}
        communes={filteredCommunes}
      />
    </div>
  )
}

interface MapProps {
  communes: ExtendedFeatureCollection
  hoverHandler: () => void
}

const MapBackground = React.memo(({ communes, hoverHandler }: MapProps) => {
  const minValue = 1 - d3.max(communes.features, (c) => c.properties.distance)
  const maxValue = 1 - d3.min(communes.features, (c) => c.properties.distance)

  const color = d3
    .scaleLinear()
    .domain([minValue, minValue + (maxValue - minValue) / 2, maxValue])
    .range(["#2e0000", "#984100", "#fa9000"])
    .interpolate(d3.interpolateRgb.gamma(2.2))

  const projection = d3.geoMercator().fitSize([width, height], communes)
  const path = d3.geoPath().projection(projection)

  const handleMouseEnter = function (e: SVGPathElement, properties: {}) {
    setTimeout(() => {
      d3.select(e).classed('active', true)
    }, 120)

    hoverHandler(properties, e.getBoundingClientRect())
  }

  const handleMouseLeave = function (e: SVGPathElement) {
    setTimeout(() => {
      d3.select(e).classed('active', false)
    }, 120)

    hoverHandler(null, null)
  }

  console.log('Draw map')

  return (
    <div className='map-background-container'>
      <svg className='mapSwitzerland' viewBox={'0 0 ' + width + ' ' + height}>
        <g className='topology'>
          {communes.features.map((feature: ExtendedFeature, i: number) => {
            return (
              <path
                key={i}
                d={path(feature)}
                fill={color(1 - feature.properties.distance)}
                onMouseEnter={(e) => {
                  handleMouseEnter(e.currentTarget, feature.properties)
                }}
                onMouseLeave={(e) => {
                  handleMouseLeave(e.currentTarget)
                }}
              />
            )
          })}
        </g>
      </svg>
      <div className='legend'>
        <div className='legend-inner ui raised segment'>
        Similarité
        <div className='colorscale'>
          <div>
            <div
              style={{
                backgroundColor: color(minValue),
              }}
            />
            {Math.round(minValue * 1000) / 10}%
          </div>
          <div
            style={{
              backgroundColor: color(minValue + (maxValue - minValue) / 5),
            }}
          />
          <div
            style={{
              backgroundColor: color(
                minValue + ((maxValue - minValue) / 5) * 2
              ),
            }}
          />
          <div
            style={{
              backgroundColor: color(
                minValue + ((maxValue - minValue) / 5) * 3
              ),
            }}
          />
          <div
            style={{
              backgroundColor: color(
                minValue + ((maxValue - minValue) / 5) * 4
              ),
            }}
          />
          <div>
            <div
              style={{
                backgroundColor: color(maxValue),
              }}
            />
            {Math.round(maxValue * 1000) / 10}%
          </div>
        </div>
      </div>
      </div>
    </div>
  )
})
